import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import useLearningContinuum from '../../../../../hooks/learning-continuum/useLearningContinuum'
import { useNavigate, useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import InputField from '../../../../FormControl/input/InputField'
import { useSelector } from 'react-redux'

const DialogAction = ({ show, onClose, type, formData = {}, onReset }) => {
  const types = ['archive', 'delete', 'clone', 'edit notes', 'batch-delete']
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {})
  const { curriculumId } = useParams()
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [errors, setInputErrors] = useState({})
  const [form, setInputForm] = useState(formData)
  const {
    deleteLearningContinua,
    batchDeleteLearningContinua,
    archiveLearningContinua,
    cloneLearningContinua,
    updateLearningContinua
  } = useLearningContinuum()

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }
  const onConfirm = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    if (form._id === undefined) {
      setInputErrors({ message: 'Invalid id' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'delete') {
        result = await deleteLearningContinua(form._id)
      }
      if (type === 'batch-delete') {
        result = await batchDeleteLearningContinua(form._id)
      }
      if (type === 'archive') {
        result = await archiveLearningContinua(form._id)
      }
      if (type === 'clone') {
        result = await cloneLearningContinua(form._id, form.email)
      }
      if (type === 'edit notes') {
        result = await updateLearningContinua(form._id, 'notes', form)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        if (type === 'delete' || type === 'archive') {
          navigate(`/dashboard/learning-continuum/${curriculumId}`)
        }
        if (type === 'batch-delete' && onReset) {
          onReset()
        }
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  useEffect(() => {
    setInputForm(formData)
    if (!formData._id) {
      setInputErrors({ message: 'Invalid id' })
    }
  }, [formData])

  return (
    <DialogConfirmCancel
      id={`learning-continuum-${type}-${form?._id}`}
      open={show}
      onCancel={onClose}
      onConfirm={types.includes(type) && form._id ? onConfirm : undefined}
      loading={loading}
      title={type === 'batch-delete' ? 'Bulk delete learning continua' : type}
      onCloseHeader={true}
      maxWidth='xs'
    >
      {type === 'batch-delete' ? (
        <div>
          <Typography>
            You are about to permanently delete <b>{form?._id?.length || 0}</b> learning continua. This is a{' '}
            <b>hard delete and cannot be undone</b>.
          </Typography>
          <Typography className='pt-2'>
            Any that are in use by a Developmental Rubric or that already have marking history will be skipped
            automatically to protect your historical records.
          </Typography>
        </div>
      ) : type === 'edit notes' ? (
        <Box>
          <div className='grid grid-cols-4 items-start gap-4'>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Notes
            </Typography>
            <div className='col-span-3'>
              <InputField
                id='notes'
                name='notes'
                value={form.notes}
                error={errors?.notes}
                helperText={errors?.notes}
                onChange={(e) => onChange('notes', e.target.value)}
                fullWidth
                multiline={true}
              />
            </div>
          </div>
        </Box>
      ) : (
        <div>
          <Typography>Are you sure you want to {type} the learning continua?</Typography>
          {currentCurriculum?.lcElements?.children?.map((item) => (
            <div className='py-1' key={item?._id}>
              <Typography
                variant='inherit'
                className='font-bold capitalize pr-4'
                color='primary'
                align='right'
                component='span'
              >
                {item?.name}
              </Typography>
              <Typography variant='inherit' component='span'>
                {form.lcElements ? form.lcElements[item?._id] : ''}
              </Typography>
            </div>
          ))}
        </div>
      )}
      {errors?.message && <Alert severity='error'>{errors?.message}</Alert>}
    </DialogConfirmCancel>
  )
}

export default DialogAction
